import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Badge,
  Tooltip,
  Divider,
} from '@mui/material';
import { FaArrowLeft, FaEye, FaEyeSlash, FaCheckSquare, FaRegCheckSquare, FaTimes, FaExchangeAlt } from 'react-icons/fa';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  HeaderButton,
  ControlsContainer,
} from '../common/styles';
import { MainContainer } from './styles';
import { useAlert } from '../../contexts/AlertContext';
import { feedbackApi } from '../../services/api';
import ResultsCount from '../common/ResultsCount';
import EmployeeSearch from '../common/EmployeeSearch';
import { ReviewType, FeedbackSessionStatus, FeedbackSessionSelector, CompletedReportsFilter, LeftIncompleteFilter, AssignmentStatus, AssignmentCategoryFilter } from '../common/filters';
import { ASSIGNMENT_CATEGORIES } from '../common/filters/AssignmentCategoryFilter';
import { FEEDBACK_SESSION_STATUS_FILTERS } from '../../constants/feedbackSessionStatuses';
import FeedbackResultsGrid from './components/FeedbackResultsGrid';
import useFeedbackResultsStore from '../../store/feedbackResultsStore';
import useGridNavigation from '../../hooks/useGridNavigation';

const FeedbackResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const gridRef = useRef(null);
  const [sessionOptions, setSessionOptions] = React.useState([]);

  // Get state and actions from Zustand store
  const {
    assignments,
    isLoading,
    isLoadingMore,
    pageInfo,
    error,
    // Filters
    selectedSession,
    selectedSessionStatuses,
    selectedReviewTypes,
    selectedEmployee,
    showCompletedOnly,
    showLeftIncompleteOnly,
    selectedAssignmentCategory,
    selectedAssignmentStatuses,
    // UI State
    showControls,
    selectionMode,
    selectedAssignments,
    // Actions
    fetchAssignmentsWithReset,
    setScrollPosition,
    fetchAssignmentsLoadMore,
    setSelectedSession,
    toggleSessionStatus,
    toggleReviewType,
    setSelectedEmployee,
    toggleCompletedOnly,
    toggleLeftIncompleteOnly,
    setSelectedAssignmentCategory,
    toggleAssignmentStatus,
    toggleControls,
    toggleSelectionMode,
    setSelectedAssignments,
    clearSelectedAssignments,
    resetState,
    setIsReturningFromDetails,
  } = useFeedbackResultsStore();

  const loadMoreCallback = useCallback(() => {
    return fetchAssignmentsLoadMore();
  }, [fetchAssignmentsLoadMore]);

  // Use the GridNavigation hook
  const {
    showLoadingSpinner,
    handleCombinedGridScroll,
    handleItemClick,
    shouldHideContent,
    loadMoreData,
    shouldLoadData,
    shouldForceReload,
  } = useGridNavigation({
    gridRef,
    items: assignments,
    columnCount: 3,
    rowHeight: 580,
    routes: {
      list: '/feedback-results',
      detail: (id) => `/feedback-sessions/assignment/${id}`,
      new: '',
      additionalRoutes: [],
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    loadMoreDataCallback: loadMoreCallback,
    pageInfo,
    isLoadingMore,
    // Note: We don't use onNavigateAway here because the detail route (/feedback-sessions/assignment/:id)
    // is in a different path section than the list route (/feedback-results).
    // Filters persist in Zustand and are preserved when navigating back from the report.
  });

  // Reset state when entering the page fresh (not from detail/comparison view)
  // This ensures filters are cleared when navigating from home or other sections
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;

      // Get current store state directly to avoid stale closure issues
      const currentState = useFeedbackResultsStore.getState();

      // Check if we're returning from a related route (detail view or comparison)
      // We detect this by:
      // 1. Explicit state flags (fromDetail, fromComparison when using explicit navigate)
      // 2. isReturningFromDetails flag set by useGridNavigation
      const isFromRelatedRoute = location.state?.fromDetail || location.state?.fromComparison || currentState.isReturningFromDetails;

      if (isFromRelatedRoute) {
        // Coming back from detail/comparison - don't reset, just restore scroll if needed
        // Data and filters are preserved in Zustand store
        // Clear the flag after checking
        if (currentState.isReturningFromDetails) {
          setIsReturningFromDetails(false);
        }
      } else {
        // Fresh navigation from home or other sections - reset state and fetch fresh data
        resetState();
        fetchAssignmentsWithReset();
      }
    }
  }, []); // Empty deps - only run once on mount

  // Scroll restoration for returning from comparison view
  // useGridNavigation handles detail view returns, but comparison needs explicit handling
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    const scrollState = location.state?.scrollState;
    const isReturning = location.state?.fromDetail && scrollState?.scrollPosition !== undefined;

    // Reset the ref when location changes (new navigation)
    if (!isReturning) {
      scrollRestoredRef.current = false;
      return;
    }

    if (!scrollRestoredRef.current && assignments.length > 0 && gridRef.current?._outerRef) {
      scrollRestoredRef.current = true;
      const targetPosition = scrollState.scrollPosition;

      // Apply scroll restoration with multiple attempts to handle race conditions
      const restoreScroll = () => {
        if (gridRef.current?._outerRef) {
          gridRef.current._outerRef.scrollTop = targetPosition;
        }
      };

      // Immediate attempt
      restoreScroll();
      // Delayed attempts to handle any layout shifts
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 150);
    }
  }, [location.state, assignments.length]);

  // Fetch session options for dropdown
  useEffect(() => {
    const fetchSessionOptions = async () => {
      try {
        const response = await feedbackApi.getSessions({ light: true });
        setSessionOptions(response);
      } catch (err) {
        showAlert(err.message || 'Failed to fetch session options', 'error', err);
      }
    };

    fetchSessionOptions();
  }, [showAlert]);

  // Show error alert when error occurs
  useEffect(() => {
    if (error) {
      showAlert(error, 'error');
    }
  }, [error, showAlert]);

  const handleBack = () => {
    // Reset filters when navigating away from the section
    resetState();
    navigate('/');
  };

  const handleViewReport = (assignmentId) => {
    // Capture scroll state before navigating
    let scrollState = null;
    if (gridRef.current && gridRef.current._outerRef) {
      const currentScrollPosition = gridRef.current._outerRef.scrollTop;
      setScrollPosition(currentScrollPosition);

      const itemIndex = assignments.findIndex(a => a.id === assignmentId);
      const rowIndex = itemIndex !== -1 ? Math.floor(itemIndex / 3) : -1;
      const itemTopPosition = rowIndex * 580;

      scrollState = {
        scrollPosition: currentScrollPosition,
        itemId: assignmentId,
        itemIndex,
        rowIndex,
        offsetFromTop: itemTopPosition - currentScrollPosition,
        timestamp: Date.now()
      };
    }

    navigate(`/feedback-sessions/assignment/${assignmentId}`, {
      state: {
        fromList: true,
        scrollState,
        returnPath: '/feedback-results'
      }
    });
  };

  const handleSelectionChange = (selectedIds) => {
    setSelectedAssignments(selectedIds);
  };

  const handleCompare = () => {
    if (selectedAssignments.length < 2) {
      showAlert('Please select at least 2 assignments to compare', 'warning');
      return;
    }

    // Capture scroll state before navigating to comparison page
    let scrollState = null;
    if (gridRef.current && gridRef.current._outerRef) {
      const currentScrollPosition = gridRef.current._outerRef.scrollTop;
      setScrollPosition(currentScrollPosition);
      scrollState = {
        scrollPosition: currentScrollPosition,
        timestamp: Date.now()
      };
    }

    navigate(`/feedback-comparison?ids=${selectedAssignments.join(',')}`, {
      state: { fromList: true, scrollState }
    });
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Feedback Results</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton
            onClick={toggleSelectionMode}
            sx={{
              color: selectionMode ? '#2563EB' : '#475569',
              borderColor: selectionMode ? '#BFDBFE' : '#E2E8F0',
              backgroundColor: selectionMode ? '#EFF6FF' : 'transparent',
              '&:hover': {
                borderColor: selectionMode ? '#93C5FD' : '#CBD5E1',
                backgroundColor: selectionMode ? '#DBEAFE' : '#F8FAFC',
              }
            }}
          >
            {selectionMode ? <FaCheckSquare /> : <FaRegCheckSquare />}
            {selectionMode ? 'Exit Selection Mode' : 'Select Assignments'}
          </HeaderButton>
          <HeaderButton
            onClick={toggleControls}
            sx={{
              color: '#475569',
              borderColor: '#E2E8F0',
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              }
            }}
          >
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>
      <Content>
        <ControlsContainer $show={showControls}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flex: 1,
              flexWrap: 'wrap'
            }}>
              <FeedbackSessionSelector
                selectedSession={selectedSession}
                onSessionChange={setSelectedSession}
                sessionOptions={sessionOptions}
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* Session Status filters */}
                <FeedbackSessionStatus
                  selectedStatuses={selectedSessionStatuses}
                  onStatusToggle={toggleSessionStatus}
                  statuses={[
                    FEEDBACK_SESSION_STATUS_FILTERS.COMPLETED,
                    FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE,
                    FEEDBACK_SESSION_STATUS_FILTERS.IN_PROGRESS
                  ]}
                />
              </Box>

              {/* Vertical Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: '32px',
                  mx: 1,
                  borderColor: '#CBD5E1'
                }}
              />

              {/* Review Type Filters */}
              <ReviewType
                selectedReviewTypes={selectedReviewTypes}
                onReviewTypeToggle={toggleReviewType}
              />

              {/* Vertical Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: '32px',
                  mx: 1,
                  borderColor: '#CBD5E1'
                }}
              />

              {/* Employee Filter */}
              <EmployeeSearch
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                includeAllOption={true}
                width={300}
                showSelectedAvatar={true}
              />

              {/* Completed Reports Filter */}
              <CompletedReportsFilter
                isSelected={showCompletedOnly}
                onToggle={toggleCompletedOnly}
              />

              {/* ADR-014 §10m: frozen-incomplete facet. A toggle rather than a
                  sixth status chip — it composes with the status row instead of
                  cross-cutting it. */}
              <LeftIncompleteFilter
                isSelected={showLeftIncompleteOnly}
                onToggle={toggleLeftIncompleteOnly}
              />

              {/* Vertical Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: '32px',
                  mx: 1,
                  borderColor: '#CBD5E1'
                }}
              />

              {/* Assignment Category Filter */}
              <AssignmentCategoryFilter
                selectedCategory={selectedAssignmentCategory}
                onCategoryChange={setSelectedAssignmentCategory}
              />

              {/* Assignment Status filters */}
              <AssignmentStatus
                selectedStatuses={selectedAssignmentStatuses}
                onStatusToggle={toggleAssignmentStatus}
              />
            </Box>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexShrink: 0
          }}>
            <ResultsCount
              count={pageInfo.totalCount}
              singular="assignment"
              plural="assignments"
            />

            {selectionMode && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedAssignments.length > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#F0F7FF',
                      border: '1px solid #DBEAFE',
                      borderRadius: '6px',
                      padding: '4px 12px',
                      gap: 1
                    }}>
                      <Badge
                        badgeContent={selectedAssignments.length}
                        color="primary"
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: '#3B82F6',
                            color: 'white',
                            fontWeight: 600
                          }
                        }}
                      >
                        <Typography sx={{ color: '#4B83F0', fontSize: '14px', fontWeight: 500 }}>
                          Selected
                        </Typography>
                      </Badge>

                      <Box sx={{
                        height: '20px',
                        borderLeft: '1px solid #DBEAFE',
                        mx: 1
                      }} />

                      <Button
                        size="small"
                        onClick={clearSelectedAssignments}
                        sx={{
                          color: '#4B83F0',
                          fontSize: '14px',
                          textTransform: 'none',
                          minWidth: 'auto',
                          padding: '0',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                        startIcon={<FaTimes size={12} />}
                      >
                        Clear
                      </Button>
                    </Box>

                    {selectedAssignments.length >= 2 && (
                      <Tooltip title="Compare selected assessments">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleCompare}
                          startIcon={<FaExchangeAlt />}
                          sx={{
                            backgroundColor: '#3B82F6',
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: '#2563EB'
                            }
                          }}
                        >
                          Compare
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ color: '#64748B', fontSize: '14px', fontStyle: 'italic' }}>
                    Click on assignments to select them
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <FeedbackResultsGrid
            assignments={assignments}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={pageInfo}
            handleItemClick={handleItemClick}
            handleGridScroll={handleCombinedGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            onViewReport={handleViewReport}
            selectable={selectionMode}
            selectedIds={selectedAssignments}
            onSelectionChange={handleSelectionChange}
          />
        </MainContainer>
      </Content>
    </Container>
  );
};

export default FeedbackResults;
